<template>
  <div class="auth-page">
    <UcariHeader />
    <main class="auth-shell auth-shell--register">
      <aside class="auth-story">
        <span class="eyebrow">Become a partner</span>
        <h1>Bring personalized wellness into your store.</h1>
        <p>Create your account to apply for wholesale access. Our partner team will review your business information and help you launch.</p>
        <div class="auth-story__stat"><strong>Up to 50%</strong><span>retail margins on eligible kit packs</span></div>
        <div class="auth-story__stat"><strong>No MOQ</strong><span>start with the order that fits your store</span></div>
      </aside>
      <form @submit.prevent="register" class="auth-card register-user">
        <div class="auth-card__heading">
          <span class="eyebrow">Partner application</span>
          <h2>Create your account</h2>
          <p>All fields are required. We’ll use this information to set up your wholesale profile.</p>
        </div>
        <div v-if="message" class="uk-alert-danger" role="alert">{{ message }}</div>
        <div>
          <div class="uk-margin">
            <label for="email">Email</label>
            <input id="email" class="uk-input" autocomplete="email" :class="{ 'error-border': (isEmailInvalid || !email) && (emailTouched || formSubmitted) }" type="email" v-model="email" @blur="validateEmail" @focus="emailTouched = true" />
            <div class="error" v-if="emailMessage">{{ emailMessage }}</div>
          </div>
          <div class="auth-form-grid">
            <div class="uk-margin">
              <label for="firstName">First name</label>
              <input id="firstName" class="uk-input" autocomplete="given-name" :class="{ 'error-border': (!firstName) && (firstNameTouched || formSubmitted) }" type="text" v-model="firstName" @focus="firstNameTouched = true" />
            </div>
            <div class="uk-margin">
              <label for="lastName">Last name</label>
              <input id="lastName" class="uk-input" autocomplete="family-name" :class="{ 'error-border': (!lastName) && (lastNameTouched || formSubmitted) }" type="text" v-model="lastName" @focus="lastNameTouched = true" />
            </div>
          </div>
          <div class="uk-margin">
            <label for="companyName">Company Name</label>
            <input
              class="uk-input"
              id="companyName"
              autocomplete="organization"
              :class="{ 'error-border': (!companyName) && (companyNameTouched || formSubmitted) }"
              type="text"
              v-model="companyName"
              @focus="companyNameTouched = true"
            />
          </div>

          <div class="uk-margin">
            <label for="phoneNumber">Phone Number</label>
            <input
              class="uk-input"
              id="phoneNumber"
              autocomplete="tel"
              :class="{ 'error-border': (isPhoneInvalid || !phoneNumber) && (phoneTouched || formSubmitted) }"
              type="tel"
              v-model="phoneNumber"
              @blur="validatePhone"
              @focus="phoneTouched = true"
              placeholder="(555) 123-4567"
            />
            <div class="error" v-if="phoneMessage">{{ phoneMessage }}</div>
          </div>
          <div class="uk-margin">
            <label for="password">Password</label>
            <div class="uk-inline">
              <input id="password" :type="showPassword ? 'text' : 'password'" autocomplete="new-password" class="uk-input" :class="{ 'error-border': (isPasswordInvalid || !password) && (passwordTouched || formSubmitted) }" v-model="password" @input="handlePasswordInput" @focus="passwordTouched = true" />
              <button type="button" class="password-toggle" @click="togglePasswordVisibility">{{ showPassword ? 'Hide' : 'Show' }}</button>
            </div>
          </div>
          <div class="password-requirements">
            <div><strong>Password must contain:</strong></div>
            <ul>
              <li :class="{ 'valid': passwordHasLowercase }">One lowercase letter</li>
              <li :class="{ 'valid': passwordHasUppercase }">One uppercase letter</li>
              <li :class="{ 'valid': passwordHasNumber }">One number</li>
              <li :class="{ 'valid': passwordHasSpecialChar }">One of these special character: ! @ # $ % ^ & * ( ) - + { } | ?</li>
              <li :class="{ 'valid': passwordHasLength }">At least 8 characters</li>
            </ul>
          </div>
          <div class="uk-margin">
            <label for="confirmPassword">Confirm Password</label>
            <div class="uk-inline">
              <input id="confirmPassword" :type="showConfirmPassword ? 'text' : 'password'" autocomplete="new-password" class="uk-input" :class="{ 'error-border': (passwordMismatch || !confirmPassword) && (confirmPasswordTouched || formSubmitted) }" v-model="confirmPassword" @input="handlePasswordInput" @focus="confirmPasswordTouched = true" />
              <button type="button" class="password-toggle" @click="toggleConfirmPasswordVisibility">{{ showConfirmPassword ? 'Hide' : 'Show' }}</button>
            </div>
            <div class="error" v-if="passwordMismatch">{{ passwordMismatch }}</div>
          </div>
          <div class="uk-margin-bottom">
            By creating an account, you agree to our <a href="https://ucari.com/termsofservice" target="_blank">Terms of Service</a> and <a href="https://ucari.com/privacy" target="_blank">Privacy Policy</a>.
          </div>
          <button class="uk-button uk-button-primary uk-width-1-1 uk-margin" type="submit" :disabled="isFormInvalid || loading">
            Create partner account
            <span v-if="loading" class="uk-margin-small-left" uk-spinner="ratio: 0.6"></span>
          </button>          
          <div class="auth-card__links auth-card__links--center">Already have a partner account? <router-link to="/login">Sign in</router-link></div>
        </div>
      </form>
    </main>
    <RetailerFooter />
  </div>
</template>

<script>
import axios from 'axios';
import UcariHeader from './components/UcariHeader.vue';
import RetailerFooter from './components/RetailerFooter.vue';
import { validatePassword, toggleVisibility } from '@/utils/common';

export default {
  created() {
    document.title = 'Create Partner Account | UCARI';
  },  
  components: {
    UcariHeader,
    RetailerFooter,
  },
  data() {
    return {
      email: '',
      firstName: '',
      lastName: '',
      companyName: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
      message: '',
      emailMessage: '',
      passwordMismatch: '',
      phoneMessage: '',
      isEmailInvalid: false,
      isPasswordInvalid: false,
      isPhoneInvalid: false,
      showPassword: false,
      showConfirmPassword: false,
      emailTouched: false,
      firstNameTouched: false,
      lastNameTouched: false,
      companyNameTouched: false,
      phoneTouched: false,
      passwordTouched: false,
      confirmPasswordTouched: false,
      formSubmitted: false,
      loading: false,
    };
  },
  computed: {
    isFormInvalid() {
      return (
        this.isEmailInvalid ||
        this.isPasswordInvalid ||
        this.isPhoneInvalid ||
        this.password !== this.confirmPassword ||
        !this.email ||
        !this.firstName ||
        !this.lastName ||
        !this.companyName ||
        !this.phoneNumber ||
        !this.password ||
        !this.confirmPassword
      );
    },
    passwordHasLowercase() {
      return /[a-z]/.test(this.password);
    },
    passwordHasUppercase() {
      return /[A-Z]/.test(this.password);
    },
    passwordHasNumber() {
      return /\d/.test(this.password);
    },
    passwordHasSpecialChar() {
      return /[!@#$%^&*()\-+{}|:;<>?/\\]/.test(this.password);
    },
    passwordHasLength() {
      const minLength = 8;
      return this.password.length >= minLength;
    }
  },
  methods: {
    async validateEmail() {
      if (this.email === "") return;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.email)) {
        this.emailMessage = 'Invalid email address';
        this.isEmailInvalid = true;
        return;
      }

      try {
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/api/check-email`, {
          email: this.email
        });
        if (response.data.exists) {
          this.emailMessage = 'Email already registered';
          this.isEmailInvalid = true;
        } else {
          this.emailMessage = '';
          this.isEmailInvalid = false;
        }
      } catch (error) {
        const msg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to validate email right now. Please try again.";

        this.emailMessage = msg;
        this.isEmailInvalid = true;
      }
    },
    validatePhone() {
      if (this.phoneNumber === "") return;

      // Lightweight: require at least 10 digits
      const digits = String(this.phoneNumber).replace(/\D/g, '');
      if (digits.length < 10) {
        this.phoneMessage = 'Please enter a valid phone number';
        this.isPhoneInvalid = true;
        return;
      }

      this.phoneMessage = '';
      this.isPhoneInvalid = false;
    },    
    handlePasswordInput() {
      const { isPasswordInvalid, passwordMismatch } = validatePassword(this.password, this.confirmPassword);
      this.isPasswordInvalid = isPasswordInvalid;
      this.passwordMismatch = passwordMismatch;
    },
    togglePasswordVisibility() {
      this.showPassword = toggleVisibility(this.showPassword);
    },
    toggleConfirmPasswordVisibility() {
      this.showConfirmPassword = toggleVisibility(this.showConfirmPassword);
    },
    async register() {
      this.formSubmitted = true;
      this.validatePhone();

      if (this.isFormInvalid) {
        this.message = 'Please correct the errors in the form';
        return;
      }

      this.loading = true;

      try {
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/api/register`, {
          username: this.email,
          email: this.email,
          firstName: this.firstName,
          lastName: this.lastName,
          companyName: this.companyName,
          phoneNumber: this.phoneNumber,
          password: this.password,
          isPartner: true
        });
        //this.message = response.data.message;
//        this.$router.push('/kit-registered');

        if (response.status === 200 && response.data.message === "Registration successful. Please check your email for verifying your email.") {
          this.$router.push('/registration-successful');
        }
      } catch (error) {
        const msg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Registration failed. Please try again.";

        this.message = `Error: ${msg}`;
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
